import React from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Toggle
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import TeamSelectDeprecated from "@/components/messaging/TeamSelect";

interface TikTokConfig {
  id: number;
  displayName: string;
  username: string;
  avatarUrl: string;
  teamId: number | null;
  autoCreateContact: boolean;
  tokenValid: boolean;
  refreshTokenExpiresAt: string | null;
}

interface Props {
  configId: number;
  initialConfig: TikTokConfig;
  onClose(): void;
  onSave(): void;
}

export default function TikTokEditModal(props: Props) {
  const api = useApi();
  const [config, setConfig] = React.useState<TikTokConfig>(props.initialConfig);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function update(changes: Partial<TikTokConfig>) {
    setConfig((prev) => ({ ...prev, ...changes }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!config) return;

    setSaving(true);
    setError(null);
    try {
      const resp = await api.messaging.put(`TikTok/Integrations/${props.configId}`, {
        teamId: config.teamId ?? 0,
        autoCreateContact: config.autoCreateContact
      });

      if (resp.ok) {
        props.onSave();
      } else {
        const data = await resp.json().catch(() => null);
        setError(data?.error ?? "Failed to save. Please try again.");
      }
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return "Unknown";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "Unknown";
    return d.toLocaleDateString();
  }

  const title = `@${config.username || config.displayName || "TikTok Account"}`;

  return (
    <Modal show={true}>
      <ModalHeader title={title} onClose={props.onClose} />
      <form onSubmit={handleSave}>
        <ModalBody>
          <div className="p-4 space-y-4">
            {/* Profile summary */}
            {config && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                {config.avatarUrl ? (
                  <img
                    src={config.avatarUrl}
                    alt={config.displayName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.19 8.19 0 0 0 4.78 1.52V6.84a4.85 4.85 0 0 1-1.01-.15z" />
                    </svg>
                  </div>
                )}
                <div>
                  <div className="font-medium text-slate-800">{config.displayName}</div>
                  {config.username && (
                    <div className="text-xs text-slate-500">@{config.username}</div>
                  )}
                  <div className="text-xs mt-0.5">
                    <span
                      className={`inline-flex items-center gap-1 ${
                        config.tokenValid ? "text-green-600" : "text-red-500"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${config.tokenValid ? "bg-green-500" : "bg-red-500"}`}
                      />
                      {config.tokenValid
                        ? `Token valid · Refresh expires ${formatDate(config.refreshTokenExpiresAt)}`
                        : "Token expired — reconnect from the TikTok page"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded">
                {error}
              </div>
            )}

            {/* Team assignment */}
            <div>
              <label htmlFor="tt-team" className="block text-sm font-medium text-slate-700 mb-1">
                Route DMs to team
              </label>
              <TeamSelectDeprecated
                id="tt-team"
                className="input w-full"
                value={config.teamId ?? undefined}
                onChange={(e) =>
                  update({ teamId: e.currentTarget.value ? parseInt(e.currentTarget.value) : null })
                }
              />
              <p className="text-xs text-slate-400 mt-1">
                Incoming DMs will be routed to this team. Leave blank to route to all agents.
              </p>
            </div>

            {/* Auto-create contact */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-700">Auto-create contact</div>
                <div className="text-xs text-slate-400">
                  Automatically create a contact record for new visitors
                </div>
              </div>
              <Toggle
                toggled={config.autoCreateContact ?? true}
                changeToggle={() => update({ autoCreateContact: !(config.autoCreateContact ?? true) })}
              />
            </div>

          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex items-center justify-end gap-3 p-4">
            <SecondaryBlueButton label="Cancel" onClick={props.onClose} />
            <PrimaryBlueButton
              label={saving ? "Saving..." : "Save"}
              type="submit"
              disabled={saving}
            />
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
